import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { AlertTriangle, RefreshCw, UserCheck, ShieldCheck } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

interface Item {
  booking_id: string;
  user_id: string;
  user_name: string;
  gym_id: string;
  gym_name: string;
  app_names: string[];
  class_date: string;
  time: string | null;
  course_name: string | null;
  cancelled_on: string | null;
  hours_before: number | null;
  current_sessions_remaining: number | null;
}

type Decision = "consumed_by_member" | "admin_cancellation";

const fetchList = async (gymFilter: string | null): Promise<Item[]> => {
  const { data, error } = await supabase.functions.invoke("cancellations-review", {
    body: { action: "list", gym_id: gymFilter || undefined },
  });
  if (error) throw error;
  return (data?.items || []) as Item[];
};

export default function CancellationsReview() {
  const { t } = useTranslation(["superadmin2", "common"]);
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  const [gymFilter, setGymFilter] = useState<string>("");
  const [pending, setPending] = useState<{ item: Item; decision: Decision } | null>(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // La liste est chargée sans filtre de salle : la filtrer côté serveur
  // vidait le menu déroulant (construit depuis `items`) dès qu'une salle
  // était choisie, rendant impossible le passage à une autre.
  const { data: items = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ["cancellations-review"],
    queryFn: () => fetchList(null),
  });

  const byGym = gymFilter ? items.filter((i) => i.gym_id === gymFilter) : items;

  const filtered = search
    ? byGym.filter((i) =>
        i.user_name.toLowerCase().includes(search.toLowerCase()) ||
        i.gym_name.toLowerCase().includes(search.toLowerCase()) ||
        (i.course_name || "").toLowerCase().includes(search.toLowerCase())
      )
    : byGym;

  const gyms = Array.from(new Map(items.map((i) => [i.gym_id, i.gym_name])).entries());

  const submit = async () => {
    if (!pending) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("cancellations-review", {
        body: {
          action: "decide",
          booking_id: pending.item.booking_id,
          decision: pending.decision,
          note: note.trim() || undefined,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(
        pending.decision === "consumed_by_member"
          ? t("cancellationsReview.markedMember")
          : t("cancellationsReview.markedAdmin")
      );
      setPending(null);
      setNote("");
      refetch();
    } catch (e: any) {
      toast.error(e?.message || t("cancellationsReview.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SuperAdminLayout>
      <div className="p-3 md:p-6 overflow-auto min-w-0">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {t("cancellationsReview.title")}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t("cancellationsReview.subtitle")}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
            {t("cancellationsReview.refresh")}
          </Button>
        </div>

        <Card className="mb-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t("cancellationsReview.filters")}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col md:flex-row gap-3">
            <Input
              placeholder={t("cancellationsReview.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="md:max-w-sm"
            />
            <select
              value={gymFilter}
              onChange={(e) => setGymFilter(e.target.value)}
              className="border border-input bg-background rounded-md h-10 px-3 text-sm"
            >
              <option value="">{t("cancellationsReview.allGyms")}</option>
              {gyms.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
            <Badge variant="secondary" className="self-start md:self-center">
              {t("cancellationsReview.casesToClarify", { count: filtered.length })}
            </Badge>
          </CardContent>
        </Card>

        {isLoading ? (
          <p className="text-muted-foreground">{t("cancellationsReview.loading")}</p>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              {t("cancellationsReview.noCasesToClarify")}
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("cancellationsReview.member")}</TableHead>
                  {!isMobile && <TableHead>{t("cancellationsReview.gymApp")}</TableHead>}
                  <TableHead>{t("cancellationsReview.course")}</TableHead>
                  {!isMobile && <TableHead>{t("cancellationsReview.cancelledOn")}</TableHead>}
                  <TableHead>{t("cancellationsReview.delay")}</TableHead>
                  {!isMobile && <TableHead>{t("cancellationsReview.currentBalance")}</TableHead>}
                  <TableHead className="text-right">{t("cancellationsReview.action")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((i) => (
                  <TableRow key={i.booking_id}>
                    <TableCell className="font-medium whitespace-nowrap">
                      {i.user_name}
                    </TableCell>
                    {!isMobile && (
                      <TableCell className="text-xs">
                        {i.app_names.length > 0 ? i.app_names.join(", ") : i.gym_name}
                      </TableCell>
                    )}
                    <TableCell className="text-xs">
                      <div>{i.course_name || "—"}</div>
                      <div className="text-muted-foreground">
                        {format(new Date(`${i.class_date}T00:00`), "dd MMM yyyy", { locale: fr })}
                        {i.time ? ` · ${i.time}` : ""}
                      </div>
                    </TableCell>
                    {!isMobile && (
                      <TableCell className="text-xs whitespace-nowrap">
                        {i.cancelled_on
                          ? format(new Date(i.cancelled_on), "dd MMM HH:mm", { locale: fr })
                          : "—"}
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge variant={i.hours_before !== null && i.hours_before <= 0 ? "destructive" : "outline"} className="text-xs">
                        {i.hours_before !== null
                          ? i.hours_before <= 0
                            ? t("cancellationsReview.afterClass")
                            : `${i.hours_before.toFixed(1)}h`
                          : "—"}
                      </Badge>
                    </TableCell>
                    {!isMobile && (
                      <TableCell className="font-mono text-xs">
                        {i.current_sessions_remaining ?? "—"}
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end flex-wrap">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => { setPending({ item: i, decision: "consumed_by_member" }); setNote(""); }}
                        >
                          <UserCheck className="h-3.5 w-3.5 mr-1" />
                          {t("cancellationsReview.memberBtn")}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => { setPending({ item: i, decision: "admin_cancellation" }); setNote(""); }}
                        >
                          <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                          {t("cancellationsReview.adminBtn")}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {pending?.decision === "consumed_by_member"
                  ? t("cancellationsReview.markMemberTitle")
                  : t("cancellationsReview.markAdminTitle")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("cancellationsReview.confirmDesc")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="my-2 text-sm">
              <div><span className="text-muted-foreground">{t("cancellationsReview.memberLabel")}</span> {pending?.item.user_name}</div>
              <div><span className="text-muted-foreground">{t("cancellationsReview.courseLabel")}</span> {pending?.item.course_name || "—"} · {pending?.item.class_date} {pending?.item.time}</div>
              <div><span className="text-muted-foreground">{t("cancellationsReview.delayLabel")}</span> {pending?.item.hours_before?.toFixed(2)}h</div>
            </div>
            <Textarea
              placeholder={t("cancellationsReview.notePlaceholder")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={3}
            />
            <AlertDialogFooter>
              <AlertDialogCancel disabled={submitting}>{t("cancellationsReview.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={submit} disabled={submitting}>
                {submitting ? t("cancellationsReview.saving") : t("cancellationsReview.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </SuperAdminLayout>
  );
}
